import React, { useState } from 'react';
import styled from 'styled-components';
import { FaEnvelope, FaInstagram, FaMapMarkerAlt, FaPhone } from 'react-icons/fa';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import emailjs from '@emailjs/browser';

const blankForm = { name: '', email: '', message: '' };

const ContactForm = () => {
  const [formData, setFormData] = useState(blankForm);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (field) => (e) =>
    setFormData(prev => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await emailjs.send(
        process.env.REACT_APP_SERVICE_ID,
        process.env.REACT_APP_TEMPLATE_ID,
        formData,
        process.env.REACT_APP_PUBLIC_KEY
      );
      toast.success('Message sent successfully! We\'ll be in touch soon.');
      setFormData(blankForm);
    } catch {
      toast.error('Failed to send message. Please try again or call us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FormSection>
      <ToastContainer position="top-right" />
      <FormInner>
        <FormCard>
          <CardLabel>Send Us a Message</CardLabel>
          <CardTitle>Let's Talk About Your Project</CardTitle>
          <StyledForm onSubmit={handleSubmit}>
            <FieldGroup>
              <Label htmlFor="name">Full Name</Label>
              <Input
                id="name"
                type="text"
                placeholder="John Smith"
                name="name"
                value={formData.name}
                onChange={handleChange('name')}
                required
              />
            </FieldGroup>
            <FieldGroup>
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                placeholder="john@example.com"
                name="email"
                value={formData.email}
                onChange={handleChange('email')}
                required
              />
            </FieldGroup>
            <FieldGroup>
              <Label htmlFor="message">Message</Label>
              <Textarea
                id="message"
                rows={6}
                placeholder="Tell us about your project..."
                name="message"
                value={formData.message}
                onChange={handleChange('message')}
                required
              />
            </FieldGroup>
            <SubmitBtn type="submit" disabled={submitting}>
              {submitting ? 'Sending...' : 'Send Message'}
            </SubmitBtn>
          </StyledForm>
        </FormCard>

        <InfoCard>
          <CardLabel>Contact Information</CardLabel>
          <CardTitle>Reach Us Directly</CardTitle>
          <InfoList>
            <InfoRow>
              <IconWrap><FaPhone /></IconWrap>
              <div>
                <InfoLabel>Phone</InfoLabel>
                <InfoLink href="tel:+18188095041">1 (818) 809-5041</InfoLink>
              </div>
            </InfoRow>
            <InfoRow>
              <IconWrap><FaMapMarkerAlt /></IconWrap>
              <div>
                <InfoLabel>Location</InfoLabel>
                <InfoText>Los Angeles, California</InfoText>
              </div>
            </InfoRow>
            <InfoRow>
              <IconWrap><FaEnvelope /></IconWrap>
              <div>
                <InfoLabel>Email</InfoLabel>
                <InfoLink href="mailto:championbuilding12@gmail.com">
                  championbuilding12@gmail.com
                </InfoLink>
              </div>
            </InfoRow>
          </InfoList>

          <Divider />

          <SocialSection>
            <SocialLabel>Follow Our Work</SocialLabel>
            <SocialRow>
              <SocialBtn
                href="https://www.instagram.com/championandchampion/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
              >
                <FaInstagram />
                <span>@championandchampion</span>
              </SocialBtn>
            </SocialRow>
          </SocialSection>

          <SpanishNote>¡Hablamos Español!</SpanishNote>
        </InfoCard>
      </FormInner>
    </FormSection>
  );
};

const FormSection = styled.div`
  background: #fff;
  padding: 72px 32px;
  @media (max-width: 768px) {
    padding: 48px 20px;
  }
`;

const FormInner = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  gap: 40px;
  align-items: start;
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const FormCard = styled.div`
  background: #f9fafb;
  border-radius: 8px;
  padding: 48px 40px;
  @media (max-width: 600px) {
    padding: 32px 24px;
  }
`;

const InfoCard = styled.div`
  background: #111827;
  border-radius: 8px;
  padding: 48px 40px;
  @media (max-width: 600px) {
    padding: 32px 24px;
  }
`;

const CardLabel = styled.p`
  font-family: "Poppins", sans-serif;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 3px;
  text-transform: uppercase;
  color: #c89432;
  margin-bottom: 8px;
`;

const CardTitle = styled.h2`
  font-family: "Poppins", sans-serif;
  font-size: 1.5rem;
  font-weight: 700;
  color: #fff;
  margin-bottom: 32px;
  ${FormCard} & {
    color: #111827;
  }
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-family: "Poppins", sans-serif;
  font-size: 0.825rem;
  font-weight: 600;
  color: #374151;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 1.5px solid #e5e7eb;
  border-radius: 6px;
  background: #fff;
  font-family: "Poppins", sans-serif;
  font-size: 0.9rem;
  color: #111827;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  outline: none;
  &::placeholder { color: #9ca3af; }
  &:focus {
    border-color: #c89432;
    box-shadow: 0 0 0 3px rgba(200, 148, 50, 0.15);
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 12px 16px;
  border: 1.5px solid #e5e7eb;
  border-radius: 6px;
  background: #fff;
  font-family: "Poppins", sans-serif;
  font-size: 0.9rem;
  color: #111827;
  resize: vertical;
  min-height: 140px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  outline: none;
  &::placeholder { color: #9ca3af; }
  &:focus {
    border-color: #c89432;
    box-shadow: 0 0 0 3px rgba(200, 148, 50, 0.15);
  }
`;

const SubmitBtn = styled.button`
  padding: 14px 32px;
  background: #c89432;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-family: "Poppins", sans-serif;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: background 0.25s ease, transform 0.2s ease;
  &:hover:not(:disabled) {
    background: #a97a20;
    transform: translateY(-1px);
  }
  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
`;

const InfoList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const InfoRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
`;

const IconWrap = styled.div`
  width: 36px;
  height: 36px;
  background: rgba(200, 148, 50, 0.15);
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #c89432;
  font-size: 0.9rem;
  flex-shrink: 0;
`;

const InfoLabel = styled.p`
  font-family: "Poppins", sans-serif;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: #6b7280;
  margin-bottom: 3px;
`;

const InfoLink = styled.a`
  font-family: "Poppins", sans-serif;
  font-size: 0.9rem;
  color: #e5e7eb;
  transition: color 0.2s ease;
  &:hover { color: #c89432; }
`;

const InfoText = styled.p`
  font-family: "Poppins", sans-serif;
  font-size: 0.9rem;
  color: #e5e7eb;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #1f2937;
  margin: 28px 0;
`;

const SocialSection = styled.div``;

const SocialLabel = styled.p`
  font-family: "Poppins", sans-serif;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: #6b7280;
  margin-bottom: 14px;
`;

const SocialRow = styled.div``;

const SocialBtn = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  background: #1f2937;
  border-radius: 6px;
  color: #e5e7eb;
  font-family: "Poppins", sans-serif;
  font-size: 0.875rem;
  font-weight: 500;
  transition: background 0.25s ease, color 0.25s ease;
  &:hover {
    background: #c89432;
    color: #fff;
  }
`;

const SpanishNote = styled.p`
  font-family: "Poppins", sans-serif;
  font-size: 0.85rem;
  font-style: italic;
  color: #c89432;
  margin-top: 20px;
`;

export default ContactForm;
